import { AudioMessage, AudioMessage_SpeakerInfo } from "../generated/audio_message";
import { WebSocketConnection } from "./WebSocketConnection";
import { createAudioUploadMessage } from "./protobuf_utils";
import { KeyType } from "../utils/MicrophoneImpl";

export type AudioMode = "push-to-talk" | "open-mic";

/**
 * AudioStepdown is a utility class that accumulates audio data until a a certain number
 * of samples (threshold) is reached or the last piece of audio data is appended, when it
 * returns the accumulated audio data as an Int16Array.
 */
export class AudioStepdown {
  private readonly threshold: number;
  private buffer: Int16Array;

  constructor(threshold: number) {
    if (threshold <= 0) {
      throw new Error("Threshold must be a positive number.");
    }
    this.threshold = threshold;
    this.buffer = new Int16Array(0);
  }

  public append(newArray: Int16Array, isLast: Boolean): Int16Array | undefined {
    const combined = new Int16Array(this.buffer.length + newArray.length);
    combined.set(this.buffer);
    combined.set(newArray, this.buffer.length);
    this.buffer = combined;

    if (this.buffer.length >= this.threshold || isLast) {
      const output = this.buffer;
      this.buffer = new Int16Array(0);
      return output;
    }
    return undefined;
  }
}

function getSpeakerFromKey(key: KeyType, speakers: AudioMessage_SpeakerInfo[]) {
  const speaker =
    speakers && speakers.length >= 0 && key >= 0 && key < speakers.length
      ? speakers[key]
      : undefined;
  return speaker;
}

/**
 * AudioSender is a utility class that manages sending audio data over WebSocket.
 * It accumulates audio into appropriately-sized clusters, it managers the 
 * sequence id and sample offset, and it handles the first message in a cluster.
 */
export class AudioSender {

  private accumulator: AudioStepdown = new AudioStepdown(512);

  private currentAudioClusterSequenceId: number = 0;
  private currentAudioClusterSampleOffset: number = 0;
  private isFirstMessageInCluster: boolean = false;
  private isDeactivating: boolean = false;

  public startNextCluster() {
    this.currentAudioClusterSequenceId += 1;
    this.currentAudioClusterSampleOffset = 0;
    this.isFirstMessageInCluster = true;
    this.isDeactivating = false;
  }

  public setIsDeactivating(isDeactivating: boolean) {
    this.isDeactivating = isDeactivating;
  }

  public sendAudioData(data: Int16Array, _isFirst: Boolean, isLast: Boolean, keyType: KeyType, sampleRate: number) {
    if (!data || data.length === 0) return;
    let audioData = this.accumulator.append(data, isLast);
    if (audioData === undefined) return;

    const ws = WebSocketConnection.getExistingConnectedInstance();
    if (!ws) { return }

    const audioMessage: AudioMessage = createAudioUploadMessage({
      waveform: audioData,
      sampleRate: sampleRate,
      clusterSeqId: this.currentAudioClusterSequenceId,
      clusterSampleOffset: this.currentAudioClusterSampleOffset,
      isClusterEnd: this.isDeactivating,
      isFirst: this.isFirstMessageInCluster,
      recordedAtMs: Date.now(),
      speaker: getSpeakerFromKey(keyType, ws.speakers),
    });

    // Send the message
    ws.sendMessage(audioMessage);

    // Increment the sample offset by the length of the data
    this.currentAudioClusterSampleOffset += audioData.length;
    this.isFirstMessageInCluster = false;
  }
}